import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { equipmentLabelStyle, chemicalLabelStyle } from './styles';

interface BasinProps {
  visible: boolean;
  isReacting: boolean;
  isPaused: boolean;
}

const Basin: React.FC<BasinProps> = ({ visible, isReacting, isPaused }) => {
  const groupRef = useRef<THREE.Group>(null);
  const bubbleRef = useRef<THREE.InstancedMesh>(null);
  const scaleRef = useRef(0);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const bubbles = useMemo(
    () =>
      Array.from({ length: 18 }, () => ({
        pos: new THREE.Vector3(
          (Math.random() - 0.5) * 0.4,
          0.05 + Math.random() * 0.25,
          (Math.random() - 0.5) * 0.4
        ),
        speed: Math.random() * 0.006 + 0.003,
        size: Math.random() * 0.018 + 0.008,
        wobble: Math.random() * Math.PI * 2,
      })),
    []
  );

  useFrame((state) => {
    if (!groupRef.current) return;
    scaleRef.current += ((visible ? 1 : 0) - scaleRef.current) * 0.06;
    groupRef.current.scale.setScalar(scaleRef.current);

    const time = state.clock.elapsedTime;

    if (bubbleRef.current && isReacting && !isPaused) {
      bubbles.forEach((b, i) => {
        b.pos.y += b.speed;
        b.pos.x += Math.sin(time * 2.5 + b.wobble) * 0.001;
        b.pos.z += Math.cos(time * 2.5 + b.wobble) * 0.001;
        if (b.pos.y > 0.38) {
          b.pos.y = 0.05;
          b.pos.x = (Math.random() - 0.5) * 0.3;
          b.pos.z = (Math.random() - 0.5) * 0.3;
          b.size = Math.random() * 0.018 + 0.008;
        }
        dummy.position.copy(b.pos);
        dummy.scale.setScalar(b.size);
        dummy.updateMatrix();
        bubbleRef.current!.setMatrixAt(i, dummy.matrix);
      });
      bubbleRef.current.instanceMatrix.needsUpdate = true;
    } else if (bubbleRef.current) {
      bubbles.forEach((_, i) => {
        dummy.scale.setScalar(0);
        dummy.updateMatrix();
        bubbleRef.current!.setMatrixAt(i, dummy.matrix);
      });
      bubbleRef.current.instanceMatrix.needsUpdate = true;
    }
  });

  return (
    <group ref={groupRef} position={[0.5, -1.15, 0]} scale={0}>
      <mesh position={[0, 0.3, -0.5]}>
        <boxGeometry args={[1.3, 0.6, 0.04]} />
        <meshStandardMaterial color="#c0d4e0" transparent opacity={0.45} roughness={0.3} />
      </mesh>
      <mesh position={[0, 0.3, 0.5]}>
        <boxGeometry args={[1.3, 0.6, 0.04]} />
        <meshStandardMaterial color="#c0d4e0" transparent opacity={0.45} roughness={0.3} />
      </mesh>
      <mesh position={[-0.65, 0.3, 0]}>
        <boxGeometry args={[0.04, 0.6, 1.0]} />
        <meshStandardMaterial color="#c0d4e0" transparent opacity={0.45} roughness={0.3} />
      </mesh>
      <mesh position={[0.65, 0.3, 0]}>
        <boxGeometry args={[0.04, 0.6, 1.0]} />
        <meshStandardMaterial color="#c0d4e0" transparent opacity={0.45} roughness={0.3} />
      </mesh>
      <mesh position={[0, 0.01, 0]}>
        <boxGeometry args={[1.3, 0.04, 1.0]} />
        <meshStandardMaterial color="#b0c4d0" roughness={0.3} />
      </mesh>
      <mesh position={[0, 0.22, 0]}>
        <boxGeometry args={[1.22, 0.28, 0.92]} />
        <meshStandardMaterial color="#88b8dc" transparent opacity={0.5} roughness={0.0} />
      </mesh>

      <instancedMesh ref={bubbleRef} args={[undefined, undefined, 18]}>
        <sphereGeometry args={[1, 8, 8]} />
        <meshStandardMaterial color="#e0f4ff" transparent opacity={0.6} roughness={0.1} />
      </instancedMesh>

      {visible && (
        <Html position={[0, 0.4, 0]} center style={{ pointerEvents: 'none' }}>
          <div style={equipmentLabelStyle}>Basin</div>
        </Html>
      )}
      {visible && (
        <Html position={[0, 0.22, 0]} center style={{ pointerEvents: 'none' }}>
          <div style={chemicalLabelStyle}>Water</div>
        </Html>
      )}
    </group>
  );
};

export default Basin;